import { OnboardingQuestionConfig, PreferenceLeaf, UserPreferences, UserPreferencesRequest } from '@Models';

const NAME_PATTERN = /^[a-z]+(_[a-z]+)*$/;

const snakeToCamel = (s: string): string => s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toValue = (q: OnboardingQuestionConfig, answer: string | string[]): PreferenceLeaf => {
  if (q.type === 'multi-select') return [answer].flat();
  const text = [answer].flat().join(', ');
  return q.template ? q.template.replace('{value}', text) : text;
};

export const mapAnswersToRequest = (
  answers: Record<string, string | string[]>,
  questions: OnboardingQuestionConfig[],
): UserPreferencesRequest => {
  const request: UserPreferencesRequest = {};

  questions.forEach((q) => {
    const answer = answers[q.key];
    if (!answer?.length || !NAME_PATTERN.test(q.targetField)) return;
    if (q.targetKey && !NAME_PATTERN.test(q.targetKey)) return;

    const value = toValue(q, answer);
    if (!q.targetKey) {
      request[q.targetField] = value;
      return;
    }
    const existing = request[q.targetField];
    request[q.targetField] = { ...(isRecord(existing) ? existing : {}), [q.targetKey]: value };
  });

  return request;
};

const extractText = (q: OnboardingQuestionConfig, stored: string): string | undefined => {
  if (!q.template) return stored;
  const pattern = q.template.split('{value}').map(escapeRegex).join('(.+)');
  return stored.match(new RegExp(`^${pattern}$`))?.[1];
};

export const mapPreferencesToAnswers = (
  prefs: UserPreferences,
  questions: OnboardingQuestionConfig[],
): Record<string, string | string[]> => {
  const answers: Record<string, string | string[]> = {};

  questions.forEach((q) => {
    const field: unknown = prefs[snakeToCamel(q.targetField)];
    let stored: unknown = field;
    if (q.targetKey) stored = isRecord(field) ? field[snakeToCamel(q.targetKey)] : undefined;

    if (q.type === 'multi-select') {
      const values = Array.isArray(stored) ? stored.filter((v): v is string => typeof v === 'string') : [];
      if (values.length) answers[q.key] = values;
      return;
    }
    if (typeof stored !== 'string' || !stored) return;
    const text = extractText(q, stored);
    if (text) answers[q.key] = text;
  });

  return answers;
};
